import { BubbleMenu as BaseBubbleMenu } from "@tiptap/react";
import { ReactNode, useCallback, useRef } from "react";
import { Instance, sticky } from "tippy.js";
import { v4 as uuid } from "uuid";

import {
  AlignHorizontalDistributeCenter,
  AlignHorizontalDistributeEnd,
  AlignHorizontalDistributeStart,
  Pencil,
  PencilOff,
} from "lucide-react";
import { MenuProps } from "../../../features/menus/types.js";
import { Icon } from "../../../features/ui/Icon.js";
import { Toolbar } from "../../../features/ui/Toolbar.js";
import { getRenderContainer } from "../../../lib/utils/index.js";
import { AudioBlockWidth } from "./AudioBlockWidth.js";
import i18next from "i18next";

export const AudioBlockMenu = ({ editor, appendTo }: MenuProps): ReactNode => {
  const menuRef = useRef<HTMLDivElement>(null);
  const tippyInstance = useRef<Instance | null>(null);

  const getReferenceClientRect = useCallback(() => {
    const renderContainer = getRenderContainer(editor, "node-audioBlock");
    const rect =
      renderContainer?.getBoundingClientRect() ||
      new DOMRect(-1000, -1000, 0, 0);

    return rect;
  }, [editor]);

  const shouldShow = useCallback(() => {
    const isActive = editor.isActive("audioBlock");

    return isActive;
  }, [editor]);

  const onAlignAudioLeft = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setAudioBlockAlign("left")
      .run();
  }, [editor]);

  const onAlignAudioCenter = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setAudioBlockAlign("center")
      .run();
  }, [editor]);

  const onAlignAudioRight = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setAudioBlockAlign("right")
      .run();
  }, [editor]);

  const onHideCaption = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setAudioBlockCaption(
        editor.isActive("audioBlock", { caption: false }) ? true : false
      )
      .run();
  }, [editor]);

  const onWidthChange = useCallback(
    (value: number) => {
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .setAudioBlockWidth(value)
        .run();
    },
    [editor]
  );

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey={`audioBlockMenu-${uuid()}`}
      shouldShow={shouldShow}
      updateDelay={0}
      tippyOptions={{
        offset: [0, 8],
        popperOptions: {
          modifiers: [{ name: "flip", enabled: false }],
        },
        getReferenceClientRect,
        onCreate: (instance: Instance) => {
          tippyInstance.current = instance;
        },
        appendTo: () => {
          return appendTo?.current;
        },
        plugins: [sticky],
        sticky: "popper",
      }}
    >
      <Toolbar.Wrapper shouldShowContent={shouldShow()} ref={menuRef}>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("toggleCaption") || "Hide/Show caption"}
          active={editor.isActive("audioBlock", { caption: false })}
          onClick={onHideCaption}
        >
          <Icon
            icon={
              editor.isActive("audioBlock", { caption: false })
                ? PencilOff
                : Pencil
            }
          />
        </Toolbar.Button>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("alignAudioLeft") || "Align audio left"}
          active={editor.isActive("audioBlock", { align: "left" })}
          onClick={onAlignAudioLeft}
        >
          <Icon icon={AlignHorizontalDistributeStart} />
        </Toolbar.Button>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("alignAudioCenter") || "Align audio center"}
          active={editor.isActive("audioBlock", { align: "center" })}
          onClick={onAlignAudioCenter}
        >
          <Icon icon={AlignHorizontalDistributeCenter} />
        </Toolbar.Button>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("alignAudioRight") || "Align audio right"}
          active={editor.isActive("audioBlock", { align: "right" })}
          onClick={onAlignAudioRight}
        >
          <Icon icon={AlignHorizontalDistributeEnd} />
        </Toolbar.Button>
        <Toolbar.Divider />
        <AudioBlockWidth
          onChange={onWidthChange}
          value={parseInt(editor.getAttributes("audioBlock").width)}
        />
      </Toolbar.Wrapper>
    </BaseBubbleMenu>
  );
};

export default AudioBlockMenu;
